/**
 * LatencyPercentilesChart - Multi-line chart showing p50, p95, p99 latency over time
 *
 * Features:
 * - Three percentile lines: p50, p95, p99
 * - Dynamic Y-axis formatting (ms to seconds)
 * - Time-based X-axis formatting
 * - Interactive tooltip with all percentiles
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import { palette } from '../../utils/designTokens';
import { serializeFilters } from '../../utils/urlBuilder';

/**
 * Format timestamp for X-axis display
 */
const formatTimestamp = (timestamp, timeWindow) => {
  if (!timestamp) return '';

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';

    // For 24h view, show time (HH:MM)
    if (timeWindow === '24h') {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }

    // For 7d/30d/90d, show date (MM/DD)
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Format latency values for Y-axis (milliseconds to seconds)
 */
const formatLatency = (value) => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}s`;
  }
  return `${value.toFixed(0)}ms`;
};

/**
 * Custom Tooltip
 */
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;

  return (
    <div
      style={{
        backgroundColor: palette.neutrals.panelQuaternary,
        border: `1px solid ${palette.neutrals.borderSubtle}`,
        borderRadius: '8px',
        padding: '12px',
        boxShadow: palette.quality.shadowCard
      }}
    >
      <div style={{ color: palette.text.primary, fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
        {new Date(data.timestamp).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </div>
      <div style={{ color: palette.text.supporting, fontSize: '12px', marginBottom: '4px' }}>
        <span style={{ color: palette.accents.primary.base, fontWeight: 600 }}>●</span> p50: <span style={{ color: palette.text.primary, fontWeight: 600 }}>{formatLatency(data.p50_ms)}</span>
      </div>
      <div style={{ color: palette.text.supporting, fontSize: '12px', marginBottom: '4px' }}>
        <span style={{ color: palette.accents.warm.base, fontWeight: 600 }}>●</span> p95: <span style={{ color: palette.text.primary, fontWeight: 600 }}>{formatLatency(data.p95_ms)}</span>
      </div>
      <div style={{ color: palette.text.supporting, fontSize: '12px', marginBottom: '8px' }}>
        <span style={{ color: palette.status.danger, fontWeight: 600 }}>●</span> p99: <span style={{ color: palette.text.primary, fontWeight: 600 }}>{formatLatency(data.p99_ms)}</span>
      </div>
      <div style={{ color: palette.text.muted, fontSize: '11px', borderTop: `1px solid ${palette.neutrals.borderSubtle}`, paddingTop: '8px' }}>
        {data.trace_count.toLocaleString()} traces
      </div>
    </div>
  );
};


const LatencyPercentilesChart = ({ data, timeWindow, appId, environment }) => {
  const navigate = useNavigate();
  const [highlightedPoint, setHighlightedPoint] = useState(null);

  // Process data - ensure all values are numbers
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map(d => ({
      timestamp: d.timestamp,
      p50_ms: Number(d.p50_ms) || 0,
      p95_ms: Number(d.p95_ms) || 0,
      p99_ms: Number(d.p99_ms) || 0,
      trace_count: Number(d.trace_count) || 0,
    }));
  }, [data]);

  // Handle point click for drilldown
  const handlePointClick = (data, percentileKey) => {
    if (!data || !data.timestamp) {
      return;
    }

    // Highlight the clicked point
    setHighlightedPoint(data.timestamp);

    // Helper to format Date for ClickHouse
    const formatForClickHouse = (date) => date.toISOString().slice(0, 19);

    const timestamp = new Date(data.timestamp);

    // Calculate time bucket end based on timeWindow (same as Cost chart)
    const endTime = new Date(
      timeWindow === '24h'
        ? timestamp.getTime() + 60 * 60 * 1000        // 1 hour
        : timeWindow === '7d'
        ? timestamp.getTime() + 4 * 60 * 60 * 1000    // 4 hours
        : timestamp.getTime() + 24 * 60 * 60 * 1000   // 24 hours (30d/90d/all-time)
    );

    const startTimeStr = formatForClickHouse(timestamp);
    const endTimeStr = formatForClickHouse(endTime);

    // Get the percentile value (in milliseconds) from the data point
    const minLatency = data[percentileKey];

    // Navigate with filters after a brief highlight
    setTimeout(() => {
      setHighlightedPoint(null);
      const filters = {
        start_time: startTimeStr,
        end_time: endTimeStr,
        min_latency: minLatency,
        sort: 'duration_desc',
        env: environment,
        range: timeWindow
      };
      const url = `/app/${appId}/traces?${serializeFilters(filters)}`;
      navigate(url);
    }, 200);
  };

  const hasData = chartData.length > 0;

  if (!hasData) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: palette.text.muted,
          fontSize: '14px'
        }}
      >
        No latency data available for the selected time window
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '280px' }}>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={palette.neutrals.borderSubtle}
            opacity={0.3}
          />
          <XAxis
            dataKey="timestamp"
            tickFormatter={(ts) => formatTimestamp(ts, timeWindow)}
            tick={{ fill: palette.text.muted, fontSize: 11 }}
            axisLine={{ stroke: palette.neutrals.borderSubtle }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatLatency}
            tick={{ fill: palette.text.muted, fontSize: 11 }}
            axisLine={{ stroke: palette.neutrals.borderSubtle }}
            tickLine={false}
            width={120}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
            iconType="line"
          />
          <Line
            type="monotone"
            dataKey="p50_ms"
            name="p50"
            stroke={palette.accents.primary.base}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: palette.accents.primary.base, cursor: 'pointer', onClick: (e, payload) => handlePointClick(payload.payload, 'p50_ms') }}
          />
          <Line
            type="monotone"
            dataKey="p95_ms"
            name="p95"
            stroke={palette.accents.warm.base}
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 5, fill: palette.accents.warm.base, cursor: 'pointer', onClick: (e, payload) => handlePointClick(payload.payload, 'p95_ms') }}
          />
          <Line
            type="monotone"
            dataKey="p99_ms"
            name="p99"
            stroke={palette.status.danger}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: palette.status.danger, cursor: 'pointer', onClick: (e, payload) => handlePointClick(payload.payload, 'p99_ms') }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LatencyPercentilesChart;
